import { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Paperclip, Upload, X, Download, FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon } from "lucide-react";

/**
 * Multi-file attachments field.  Renders a list of already-attached files
 * (name, size, download, delete) and a Choose/Drop file button that immediately
 * uploads to the parent record.  Requires an existing parent id — pass
 * `parentId={null}` before save and the field falls back to a hint.
 *
 *   uploadUrl:  POST endpoint that accepts multipart `file` and returns the
 *               new attachment record.
 *   deleteUrl(att):  function returning the DELETE endpoint for a given attachment.
 *   attachments:  array of {id, name, size, content_type, storage_path}
 *   onChange:  called with new full array after every mutation
 *   readOnly:  hide add + delete buttons
 */
const KB = 1024, MB = 1024 * 1024;
const humanSize = (b) => (b >= MB ? `${(b / MB).toFixed(1)} MB` : b >= KB ? `${Math.round(b / KB)} KB` : `${b} B`);

const iconFor = (mime = "", name = "") => {
  const s = `${mime} ${name}`.toLowerCase();
  if (s.includes("image")) return ImageIcon;
  if (s.includes("pdf")) return FileText;
  if (s.includes("sheet") || s.includes("excel") || s.endsWith(".csv")) return FileSpreadsheet;
  if (s.includes("word") || s.includes("doc")) return FileText;
  return FileIcon;
};

export default function AttachmentsField({
  parentId,
  uploadUrl,
  deleteUrl,
  attachments = [],
  onChange,
  readOnly = false,
  label = "Attachments",
  hint = "PDF, images, DOC, XLS — up to 25 MB each.",
}) {
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const uploadFile = async (file) => {
    if (!parentId) return toast.error("Save the record first, then attach files");
    if (file.size > 25 * MB) return toast.error(`"${file.name}" exceeds 25 MB`);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(uploadUrl(parentId), fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.([...(attachments || []), r.data]);
      toast.success(`Uploaded ${file.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Upload failed for ${file.name}`);
    } finally { setBusy(false); }
  };

  const onFiles = async (files) => {
    for (const f of Array.from(files || [])) {
      // upload sequentially so we don't blow the pod's memory
      await uploadFile(f);
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (att) => {
    if (!window.confirm(`Delete "${att.name}"?`)) return;
    try {
      await api.delete(deleteUrl(parentId, att.id));
      onChange?.((attachments || []).filter((a) => a.id !== att.id));
      toast.success("Attachment removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const openDownload = (att) => {
    const token = localStorage.getItem("mv_token");
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/attachments/download?path=${encodeURIComponent(att.storage_path)}&name=${encodeURIComponent(att.name)}&auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const hasAny = (attachments || []).length > 0;

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-[12px] font-medium">
          <span className="inline-flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> {label}</span>
          {hasAny && <span className="ml-2 text-[10px] font-mono-fin text-slate-400">{attachments.length}</span>}
        </Label>
        {!readOnly && parentId && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => onFiles(e.target.files)}
              data-testid="att-file-input"
            />
            <Button type="button" variant="outline" size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="h-8 rounded-md text-[11px] gap-1"
              data-testid="att-choose">
              <Upload className="h-3 w-3" /> {busy ? "Uploading…" : "Choose files"}
            </Button>
          </>
        )}
      </div>

      {!parentId && (
        <div className="mt-2 text-[11px] text-slate-500 border border-dashed border-slate-200 rounded-md p-3 bg-slate-50">
          Save this record first, then come back to attach files.
        </div>
      )}

      {parentId && !readOnly && (
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            onFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={`mt-2 border-2 border-dashed rounded-md px-3 py-4 flex items-center justify-center gap-2 cursor-pointer transition-colors text-[12px] ${
            dragging ? "border-red-500 bg-red-50/40 text-red-700" : "border-slate-200 bg-slate-50/50 text-slate-500 hover:border-slate-300"
          }`}
          data-testid="att-dropzone"
        >
          <Upload className="h-4 w-4" />
          <span>Drop files here or <span className="text-red-600 font-medium">click to browse</span></span>
        </div>
      )}

      {hasAny && (
        <ul className="mt-2 space-y-1.5" data-testid="att-list">
          {attachments.map((a) => {
            const Icn = iconFor(a.content_type, a.name);
            return (
              <li key={a.id}
                className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-2 bg-white text-[12px] hover:border-slate-300"
                data-testid={`att-${a.id}`}>
                <div className="h-7 w-7 shrink-0 flex items-center justify-center rounded-sm bg-slate-100 text-slate-600">
                  <Icn className="h-3.5 w-3.5" />
                </div>
                <button type="button" onClick={() => openDownload(a)}
                  className="flex-1 text-left min-w-0 hover:text-red-700 group">
                  <div className="truncate font-medium text-slate-900 group-hover:text-red-700">{a.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono-fin">
                    {humanSize(a.size)} · {(a.uploaded_at || "").slice(0, 10)}
                  </div>
                </button>
                <button type="button" onClick={() => openDownload(a)}
                  title="Download / open"
                  className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
                  data-testid={`att-dl-${a.id}`}>
                  <Download className="h-3.5 w-3.5" />
                </button>
                {!readOnly && (
                  <button type="button" onClick={() => remove(a)}
                    title="Delete"
                    className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
                    data-testid={`att-rm-${a.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!hasAny && parentId && (
        <div className="mt-2 text-[10.5px] text-slate-400">{hint}</div>
      )}
    </div>
  );
}
